/** Sidebar panel, backdrop, header, and icon badge styles. */
import { cn } from "@/utils/cn";
import type { SidebarSide, SidebarWidth } from "./Sidebar.types";

/** Width token → Tailwind. */
const widthClasses: Record<SidebarWidth, string> = {
  xs: "w-full sm:w-1/4",
  sm: "w-full sm:w-1/3",
  md: "w-full sm:w-1/2",
  lg: "w-full sm:w-2/3",
  xl: "w-full sm:w-3/4",
  full: "w-full",
};

/** Side → position (top/right/left/bottom). */
const sidePositionClasses: Record<SidebarSide, string> = {
  right: "top-0 right-0 h-full",
  left: "top-0 left-0 h-full",
  top: "top-0 left-0 w-full",
  bottom: "bottom-0 left-0 w-full",
};

/** Side → open/closed transform. */
const sideTransformClasses: Record<
  SidebarSide,
  { open: string; closed: string }
> = {
  right: { open: "translate-x-0", closed: "translate-x-full" },
  left: { open: "translate-x-0", closed: "-translate-x-full" },
  top: { open: "translate-y-0", closed: "-translate-y-full" },
  bottom: { open: "translate-y-0", closed: "translate-y-full" },
};

/** Backdrop: overlay, opacity from isOpen. */
export function getBackdropClasses(
  isOpen: boolean,
  /** Optional merge. */
  className?: string
): string {
  return cn(
    "fixed inset-0 bg-black/30 z-[100] transition-opacity duration-300",
    isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
    className
  );
}

/** Panel: position, width, slide transform. */
export function getPanelClasses(
  side: SidebarSide,
  width: SidebarWidth,
  isOpen: boolean,
  /** Optional merge. */
  className?: string
): string {
  const position = sidePositionClasses[side];
  const dimension = side === "top" || side === "bottom" ? "" : widthClasses[width];
  const transform = sideTransformClasses[side][isOpen ? "open" : "closed"];

  return cn(
    "fixed",
    position,
    dimension,
    "bg-white z-[101] shadow-2xl transform transition-transform duration-300 ease-in-out",
    transform,
    className
  );
}

/** Header bar: icon + title + close. */
export function getHeaderClasses(
  /** Optional merge. */
  className?: string
): string {
  return cn(
    "flex items-center justify-between px-4 sm:px-6 py-4 sm:py-4.5 border-b border-gray-200 bg-white",
    className
  );
}

/** Icon wrapper: gradient badge. */
export function getIconBadgeClasses(
  /** Optional merge. */
  className?: string
): string {
  return cn(
    "w-8 h-8 rounded-lg bg-gradient-to-br from-gradient-purple to-gradient-pink flex items-center justify-center flex-shrink-0",
    className
  );
}
